import { Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { FilterGroup } from '@tv3/services/utils/filters.service'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { combineLatest, Subject } from 'rxjs'
import { formatDate, isEmptyTable, localeCompareSort } from '@tokeet-frontend/tv3-platform'
import { map } from 'rxjs/operators'
import { Channel, isSomething, LoadChannels, Rental, Toaster, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { ConnectionStatusTexts, ConnectionTypeTexts, ConnectionView } from '@tv3/store/connection/connection.view'
import { ConnectionFilters } from '@tv3/store/connection/connection.filters'
import { DeleteCalendarConnectionsComplete } from '@tv3/store/connection/connection.actions'
import {
  OpenChannelConnectionDialog,
  OpenChannelConnectionWizardDialog,
  OpenChannelDialog,
  OpenRentalOverlay,
} from '@tv3/store/overlay/overlay.actions'
import { ConnectionHelperService } from '@tv3/containers/channels/channel-connect/connection-helper.service'
import { LoadConnections } from '@tv3/store/connection/connection.actions'
import { Actions, ofType } from '@ngrx/effects'
import { isFeedChannel } from '@tokeet-frontend/channels'
import { LoadChannelPropertySettings } from '@tv3/store/channel-property-setting/channel-property-setting.actions'
import { AuthService } from '@tv3/services/auth.service'
import { SelectableRow } from '@tokeet-frontend/tv3-platform'

import { UserStorage } from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'
import { selectConnectionsLoaded } from '@tv3/store/connection/connection.selectors'
import { Connection } from '@tv3/store/connection/connection.model'
import { hasListingWizard } from '../channel-connect/listing-connect-wizards/index.service'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { ChannelConnectWizardsService } from '../channel-connect/channel-connect-wizards/index.service'

@Component({
  selector: 'app-connection-list',
  templateUrl: './connection-list.component.html',
  styleUrls: ['./connection-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ConnectionListComponent extends SelectableRow<ConnectionView> implements OnInit {
  defaultRental: Rental
  @Input() set rental(value: Rental) {
    this.defaultRental = value
    this.filters?.patchValue({ rentals: [value.id], channelType: '' })
  }

  selectableRowIdPropName = 'id'
  isSelectAllRenderedItems = true
  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  displayedColumns = [
    'select',
    'channelFriendlyName',
    'typeText',
    'linkDateOrCreated',
    'rentalName',
    'propertyNameView',
    'statusText',
    'edit',
  ]

  viewType = 'list'

  // filters
  filters = this.connectionFilters.group
  channelTypeCtrl = this.filters.get('channelType')
  statusTexts = ConnectionStatusTexts
  typeTexts = ConnectionTypeTexts

  // empty table
  isEmptyTable$ = isEmptyTable(this.dataSource)
  filterGroup = FilterGroup.Connection
  tableType = TableType.ChannelConnections
  isLoading$ = this.store.pipe(
    select(selectConnectionsLoaded),
    map((loaded) => !loaded)
  )
  isEmpty$ = this.connectionFilters.group.origin.pipe(map((items) => !items.length))

  refreshStatus$ = new Subject<Connection[]>()

  constructor(
    private store: Store<fromRoot.State>,
    private actions$: Actions,
    private auth: AuthService,
    private toaster: Toaster,
    protected storage: UserStorage,
    public connectionFilters: ConnectionFilters,
    private connectionHelperService: ConnectionHelperService,
    private amplitudeService: AmplitudeService,
    private channelConnectWizards: ChannelConnectWizardsService
  ) {
    super(storage, TableType.ChannelConnections)
  }

  ngOnInit() {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort

    this.connectionFilters.group.currentFilters.pipe(untilDestroy(this)).subscribe(() => {
      this.paginator.firstPage()
    })

    this.actions$.pipe(ofType(DeleteCalendarConnectionsComplete)).subscribe(() => {
      this.selection.clear()
    })

    combineLatest([this.connectionFilters.group.changes, this.connectionFilters.rentals$])
      .pipe(
        map(([connections, rentals]) => {
          if (this.auth.isAdmin()) return connections
          // const userId = this.auth.user.id;
          // const restrictedRentalId = lodash.filter(rentals, r => lodash.includes(r.restrictedUsers, userId)).map(r=>r.id)
          const rentalIds = lodash.map(rentals, (r) => r.id)

          return lodash.filter(connections, (c) => rentalIds.includes(c.rentalId))
        }),
        untilDestroy(this)
      )
      .subscribe((connections: ConnectionView[]) => {
        this.dataSource.data = connections
      })
  }

  isRowSelectable(row: ConnectionView) {
    return row.isICal || (row.isApi && !row.isFeedApi)
  }

  onMasterToggle(checked: boolean) {
    if (checked) {
      this.isSelectAllRenderedItems = true
      this.masterToggle(true)
    } else {
      this.unselectAll()
    }
  }

  onSelectAll(all = false) {
    this.unselectAll()
    if (all) {
      this.masterToggle(false)
      this.isSelectAllRenderedItems = false
    } else {
      this.masterToggle(true)
      this.isSelectAllRenderedItems = true
    }
  }

  onRefresh() {
    this.amplitudeService.logEvent('channels-list-refresh')
    this.store.dispatch(LoadChannels())
    this.store.dispatch(LoadConnections({}))
    this.store.dispatch(LoadChannelPropertySettings())
  }

  onSearch(term: string) {
    this.amplitudeService.logEvent('channels-list-search')
    this.paginator.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  isFeed(connection: ConnectionView) {
    return isFeedChannel(connection.channel?.name)
  }

  onConnectionDetail(connection: ConnectionView, wizard = true) {
    this.amplitudeService.logEvent('channels-list-connection-detail')
    this.store.dispatch(OpenChannelConnectionDialog({ data: { connection, wizard } }))
  }

  hasWizard(connection: ConnectionView) {
    // TV4-6062: no wizard for a mapping whose room no longer exists on the channel — the wizard
    // would only link/push against the dead room id. The detail drawer handles inspect/unlink.
    return hasListingWizard(connection.channel?.name) && !connection.roomMissing
  }

  onOpenWizard(connection: ConnectionView) {
    this.amplitudeService.logEvent('channels-list-open-wizard')
    this.store.dispatch(OpenChannelConnectionWizardDialog({ data: { channel: connection.channel, connection } }))
  }

  onChannelDetails(channel: Channel) {
    this.amplitudeService.logEvent('channels-list-channel-detail')
    this.store.dispatch(OpenChannelDialog({ data: { channel } }))
  }

  onRentalDetails(rental: Rental) {
    this.amplitudeService.logEvent('channels-list-rental-detail')
    this.store.dispatch(OpenRentalOverlay({ rental }))
  }

  onLinkRental(connection: ConnectionView) {
    this.amplitudeService.logEvent('channels-list-link-rental')
    this.connectionHelperService.linkRental(connection.channel.name, connection).subscribe()
  }

  onUnlinkRental(connection: ConnectionView) {
    this.amplitudeService.logEvent('channels-list-unlink-rental')
    this.connectionHelperService.unlinkRental(connection.channel.name, connection)
  }

  onUnlinkRentals() {
    const selected = this.getSelected().filter((t) => t.isApi && !t.isFeedApi && t.rentalId)
    if (selected.length < 1) {
      this.toaster.warning($localize`Please select at least one API connection.`)
      return
    }
    this.amplitudeService.logEvent('channels-list-unlink-rentals')
    this.connectionHelperService.unlinkRentals(selected)
  }

  onRemove(connection: ConnectionView) {
    this.amplitudeService.logEvent('channels-list-remove')
    if (connection.isImported) {
      this.connectionHelperService.removeImportedCalendar(connection)
    } else {
      this.connectionHelperService.removeExportedCalendar(connection)
    }
  }

  onDeleteSelected() {
    const selected = this.getSelected().filter((t) => t.isICal)
    if (selected.length < 1) {
      this.toaster.warning($localize`Please select at least one iCal connection.`)
      return
    }
    this.amplitudeService.logEvent('channels-list-delete-selected')
    this.connectionHelperService.removeCalendars(selected)
  }

  getStatusChangedTime(time: number) {
    if (isSomething(time)) {
      return $localize`Last Change: ` + formatDate(time, 'datetime')
    } else {
      return ''
    }
  }

  // A disabled connection tells the user nothing on its own, so lead with why it went off.
  getStatusTooltip(connection: ConnectionView) {
    return [
      connection.statusReasonText,
      connection.importErrorText,
      connection.importProgressText,
      this.getStatusChangedTime(connection.statusChanged),
    ]
      .filter(isSomething)
      .join('\n')
  }

  onReconnect(connection: ConnectionView) {
    this.channelConnectWizards.connect(connection.channel).subscribe(() => {
      this.store.dispatch(LoadConnections({ force: true }))
    })
  }
}
